/* frontend/src/components/Texpact/OrderDetails.jsx */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { texpactAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import MatrixView from './MatrixView';
import './OrderDetails.css';

// ✅ Main component for token-based order viewing
const OrderDetailsPage = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrderByPublicId();
  }, [publicId]);

  const loadOrderByPublicId = async () => {
    try {
      setLoading(true);
      const response = await texpactAPI.getByPublicId(publicId);
      
      if (response.data.success) {
        setOrderData(response.data.order);
        setError(null);
      } else {
        setError('Invalid or expired link');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Navigate back with preserved state
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, {
        state: {
          partnerId: location.state.partnerId,
          returnState: location.state.returnState
        }
      });
    } else {
      navigate('/orders');
    }
  };

  if (loading) {
    return (
      <div className="order-details">
        <div className="loading">Loading order details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-details">
        <div className="error-message">
          <h3>⚠️ Access Denied</h3>
          <p>{error}</p>
          <button onClick={handleBack} className="btn-back">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return <Navigate to="/orders" replace />;
  }

  return (
    <OrderDetails 
      poCode={orderData.header.po_code}
      onBack={handleBack}
      preloadedData={orderData}
    />
  );
};

// ✅ Component for displaying order details
const OrderDetails = ({ poCode, onBack, preloadedData }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState(preloadedData?.header || null);
  const [lines, setLines] = useState(preloadedData?.lines || []);
  const [loading, setLoading] = useState(!preloadedData);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (!preloadedData) {
      fetchOrderDetails();
    } else {
      if (preloadedData.lines && preloadedData.lines.length > 0) {
        setSelectedProduct(preloadedData.lines[0].product_code);
      }
    }
  }, [poCode, preloadedData]);

  const fetchOrderDetails = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const response = await texpactAPI.getDetails(poCode);
      setOrder(response.data.header);
      setLines(response.data.lines);
      
      if (!isRefresh && response.data.lines.length > 0) {
        setSelectedProduct(response.data.lines[0].product_code);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(t('messages.error_load', 'Failed to load order details'));
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const getUniqueProducts = () => {
    if (!lines || lines.length === 0) return [];
    
    const productsMap = new Map();
    lines.forEach(line => {
      if (!productsMap.has(line.product_code)) {
        productsMap.set(line.product_code, {
          product_code: line.product_code,
          description: line.description
        });
      }
    });
    
    return Array.from(productsMap.values());
  };

  const uniqueProducts = getUniqueProducts();

  if (loading) {
    return (
      <div className="order-details">
        <div className="loading">{t('loading', 'Loading...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-details">
        <div className="error">{error}</div>
        <button onClick={onBack} className="btn-back">
          {t('orders.back_to_list', 'Back to Orders')}
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details">
        <div className="error">{t('orders.not_found', 'Order not found')}</div>
        <button onClick={onBack} className="btn-back">
          {t('orders.back_to_list', 'Back to Orders')}
        </button>
      </div>
    );
  }

  return (
    <div className="order-details">
      {/* Header */}
      <div className="order-details-header">
        <button onClick={onBack} className="btn-back">
          ← {t('orders.back_to_list', 'Back to Orders')}
        </button>
        <h2>{t('orders.details_title', 'Order Details')}: {order.po_code}</h2>
      </div>

      {/* Order Info Card - Compact single line */}
      <div className="order-info-card">
        <div className="tex-info-row">
          <div className="tex-info-item">
            <label>{t('orders.supplier', 'Fornecedor')}:</label>
            <span>{order.supplier_name}</span>
          </div>
          <div className="tex-info-item">
            <label>{t('orders.supplier_code', 'Cód. Fornecedor')}:</label>
            <span>{order.supplier_code}</span>
          </div>
          <div className="tex-info-item">
            <label>{t('status', 'Status')}:</label>
            <span className={`status-badge status-${order.status.toLowerCase()}`}>
              {t(`status.${order.status.toLowerCase()}`, order.status)}
            </span>
          </div>
          <div className="tex-info-item">
            <label>{t('orders.delivery_date', 'Data Entrega')}:</label>
            <span>{new Date(order.delivery_date).toLocaleDateString('pt-PT')}</span>
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      {uniqueProducts.length > 0 && (
        <div className="product-tabs">
          <h3>{t('orders.products', 'Products')} ({uniqueProducts.length})</h3>
          <div className="tabs-container">
            {uniqueProducts.map(product => (
              <button
                key={product.product_code}
                className={`tab-button ${selectedProduct === product.product_code ? 'active' : ''}`}
                onClick={() => setSelectedProduct(product.product_code)}
              >
                <div className="tab-code">{product.product_code}</div>
                <div className="tab-desc">{product.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Matrix View */}
      {selectedProduct && (
        <MatrixView
          poCode={poCode}
          productCode={selectedProduct}
          onShipmentCreated={() => fetchOrderDetails(true)}
        />
      )}
    </div>
  );
};

// ✅ Export both components
export { OrderDetailsPage };
export default OrderDetails;